import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';
import ProductionThread from '../components/ProductionThread';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Clock, CheckCircle, XCircle, AlertTriangle, Eye, Search,
  Filter, X, FileText, PenLine, Loader2, MessageSquare, Paperclip, Upload,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  pending: { label: 'بانتظار المراجعة', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  approved: { label: 'مقبول', icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200' },
  rejected: { label: 'مرفوض', icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200' },
  changes_requested: { label: 'مطلوب تعديلات', icon: AlertTriangle, color: 'text-orange-600 bg-orange-50 border-orange-200' },
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'الكل' },
  { value: 'pending', label: 'بانتظار المراجعة' },
  { value: 'approved', label: 'مقبول' },
  { value: 'rejected', label: 'مرفوض' },
  { value: 'changes_requested', label: 'مطلوب تعديلات' },
];

const AdminProductionsPage = () => {
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewProd, setViewProd] = useState(null);
  const [reviewProd, setReviewProd] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAttachment, setReviewAttachment] = useState(null); // {url, name}
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const attachRef = useRef(null);

  useEffect(() => { loadProductions(); }, []);

  const loadProductions = async () => {
    try {
      const res = await api.get('/admin/productions');
      setProductions(res.data);
    } catch { toast.error('فشل تحميل النتاجات'); }
    finally { setLoading(false); }
  };

  const handleReview = async () => {
    if (!reviewStatus) { toast.error('يرجى اختيار الحالة'); return; }
    setSubmitting(true);
    try {
      await api.put(`/admin/productions/${reviewProd.id}/review`, {
        status: reviewStatus,
        admin_notes: reviewNotes,
        admin_attachment_url: reviewAttachment?.url || '',
        admin_attachment_name: reviewAttachment?.name || '',
      });
      toast.success('تمت المراجعة بنجاح');
      setReviewProd(null);
      setReviewStatus('');
      setReviewNotes('');
      setReviewAttachment(null);
      loadProductions();
    } catch { toast.error('فشلت المراجعة'); }
    finally { setSubmitting(false); }
  };

  const handleReviewAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAttach(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const isImage = /^image\//.test(file.type);
      const endpoint = isImage ? '/upload/image' : '/upload/document';
      const res = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`;
      setReviewAttachment({ url, name: res.data.filename || file.name });
      toast.success('تم رفع المرفق');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل رفع المرفق');
    } finally {
      setUploadingAttach(false);
      if (attachRef.current) attachRef.current.value = '';
    }
  };

  const filtered = productions.filter((p) => {
    if (filter !== 'all' && p.status !== filter) return false;
    if (search && !p.title.includes(search) && !p.reader_name?.includes(search) && !p.reader_number?.includes(search)) return false;
    return true;
  });

  const counts = {
    all: productions.length,
    pending: productions.filter(p => p.status === 'pending').length,
    approved: productions.filter(p => p.status === 'approved').length,
    rejected: productions.filter(p => p.status === 'rejected').length,
    changes_requested: productions.filter(p => p.status === 'changes_requested').length,
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-16 h-16 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-productions-page">
      <div>
        <h1 className="text-4xl font-bold text-accent">مراجعة النتاجات</h1>
        <p className="text-muted-foreground text-lg mt-1">مراجعة وتقييم نتاجات المشتركين</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`p-3 rounded-xl border-2 text-center transition-all ${filter === opt.value ? 'border-primary bg-primary/5 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
            data-testid={`filter-${opt.value}`}
          >
            <p className="text-2xl font-bold text-accent">{counts[opt.value] || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">{opt.label}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالعنوان أو اسم القارئ أو الرقم..."
          className="pr-10"
          data-testid="search-productions"
        />
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Filter className="w-16 h-16 text-muted mx-auto mb-4 opacity-30" />
          <p className="text-lg text-muted-foreground">لا توجد نتاجات مطابقة</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((prod) => {
            const st = STATUS_MAP[prod.status] || STATUS_MAP.pending;
            const Icon = st.icon;
            return (
              <Card key={prod.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`admin-prod-${prod.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl border ${st.color} shrink-0`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 className="font-bold text-lg text-accent truncate">{prod.title}</h3>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${st.color} shrink-0`}>{st.label}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span className="font-medium text-accent/80">{prod.reader_name || prod.reader_number}</span>
                      <span>#{prod.reader_number}</span>
                      <span>{prod.content_type === 'file' ? 'ملف مرفق' : 'نص مكتوب'}</span>
                      <span>{new Date(prod.created_at).toLocaleDateString('ar')}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${prod.want_publish === 'لا' ? 'bg-gray-50 text-gray-500 border border-gray-200' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                        {prod.want_publish === 'لا' ? 'لا يرغب بالنشر' : 'يرغب بالنشر'}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${prod.show_name === 'لا' ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'}`}>
                        {prod.show_name === 'لا' ? 'لا يرغب بإظهار اسمه' : 'يرغب بإظهار اسمه'}
                      </span>
                    </div>
                    {prod.admin_notes && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                        <p className="font-semibold text-blue-800 mb-1">ملاحظات سابقة:</p>
                        <div className="answer-html text-blue-900" dir="rtl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(prod.admin_notes) }} />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setViewProd(prod)} data-testid={`view-admin-prod-${prod.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={() => { setReviewProd(prod); setReviewStatus(''); setReviewNotes(''); }} className="bg-primary hover:bg-primary/90" data-testid={`review-prod-${prod.id}`}>
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Modal */}
      {viewProd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-accent">{viewProd.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">بواسطة: {viewProd.reader_name} - #{viewProd.reader_number}</p>
              </div>
              <button onClick={() => setViewProd(null)} data-testid="close-view-modal"><X className="w-6 h-6 text-muted-foreground" /></button>
            </div>
            {viewProd.content_type === 'file' && viewProd.file_url ? (
              <div>
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-6 h-6 text-primary" />
                  <span className="font-medium">{viewProd.file_name || 'ملف مرفق'}</span>
                  <a href={viewProd.file_url} target="_blank" rel="noreferrer" className="mr-auto text-primary underline text-sm">فتح في نافذة جديدة</a>
                </div>
                <iframe src={`${viewProd.file_url}#toolbar=1`} title={viewProd.title} className="w-full border-0 rounded-lg" style={{ height: '60vh' }} />
              </div>
            ) : (
              <div className="prose prose-lg max-w-none" dir="rtl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewProd.content || '<p>لا يوجد محتوى</p>') }} />
            )}
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewProd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-accent">مراجعة النتاج</h2>
              <button onClick={() => setReviewProd(null)} data-testid="close-review-modal"><X className="w-6 h-6 text-muted-foreground" /></button>
            </div>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-bold text-accent">{reviewProd.title}</p>
              <p className="text-sm text-muted-foreground">{reviewProd.reader_name} - #{reviewProd.reader_number}</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">القرار</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setReviewStatus('approved')}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${reviewStatus === 'approved' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    data-testid="review-approve"
                  >
                    <CheckCircle className="w-6 h-6" />
                    <span className="text-sm font-semibold">قبول</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewStatus('changes_requested')}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${reviewStatus === 'changes_requested' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    data-testid="review-changes"
                  >
                    <AlertTriangle className="w-6 h-6" />
                    <span className="text-sm font-semibold">تعديلات</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewStatus('rejected')}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${reviewStatus === 'rejected' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    data-testid="review-reject"
                  >
                    <XCircle className="w-6 h-6" />
                    <span className="text-sm font-semibold">رفض</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">ملاحظات اللجنة العلمية (تحرير غني)</label>
                <RichTextEditor value={reviewNotes} onChange={setReviewNotes} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">ملف مرفق للقارئ (اختياري)</label>
                {reviewAttachment ? (
                  <div className="flex items-center justify-between border-2 border-green-200 bg-green-50 rounded-xl p-3">
                    <span className="flex items-center gap-2 text-sm text-green-800 font-semibold">
                      <FileText className="w-4 h-4" />
                      {reviewAttachment.name}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => setReviewAttachment(null)} className="text-red-500 border-red-200" data-testid="remove-review-attachment">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => attachRef.current?.click()}
                    className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    data-testid="upload-review-attachment"
                  >
                    {uploadingAttach ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /> : (
                      <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                        <Upload className="w-4 h-4" /> اضغط لرفع ملف أو صورة للقارئ
                      </div>
                    )}
                  </div>
                )}
                <input
                  ref={attachRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                  className="hidden"
                  onChange={handleReviewAttach}
                />
              </div>

              <div className="flex gap-3">
                <Button onClick={handleReview} disabled={submitting || !reviewStatus} className="flex-1 bg-primary hover:bg-primary/90" data-testid="submit-review-btn">
                  {submitting ? 'جارٍ الإرسال...' : 'إرسال المراجعة'}
                </Button>
                <Button variant="outline" onClick={() => setReviewProd(null)} className="flex-1">إلغاء</Button>
              </div>

              {/* Persistent messaging thread */}
              <div className="pt-4">
                <ProductionThread
                  productionId={reviewProd.id}
                  viewerType="admin"
                  adminNotes={reviewProd.admin_notes}
                  adminAttachmentUrl={reviewProd.admin_attachment_url}
                  adminAttachmentName={reviewProd.admin_attachment_name}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductionsPage;
